/**
 * The trips module's paths.
 *
 * The history view is ONE route with the vehicle and the range in the query string,
 * rather than `/trips/:vehicleId/:from/:to`. Three reasons, in order of weight: the
 * URL a dispatcher pastes into a ticket then carries the whole question ("this van,
 * these two days") and reproduces exactly; changing vehicle keeps the range and vice
 * versa, with no route juggling; and a static `/trips/mileage` cannot be shadowed by
 * a `/trips/:vehicleId` pattern, which is a class of bug that only shows up once
 * somebody registers a vehicle whose id is the word "mileage".
 *
 * Route registration lives in `routes/app-routes.tsx` — see NOTES-PHASE3.md §1.
 */

export const TRIPS_PATH = '/trips'
export const TRIP_REPLAY_PATH = '/trips/replay/:tripId'
export const MILEAGE_REPORT_PATH = '/trips/mileage'

export const TRIPS_QUERY_KEYS = {
  vehicle: 'vehicle',
  from: 'from',
  to: 'to',
} as const

export interface HistoryLinkParams {
  vehicleId?: string
  /** `YYYY-MM-DD`, in the tenant's timezone — the same day keys the view groups by. */
  from?: string
  to?: string
}

export function historyPath(params: HistoryLinkParams = {}): string {
  const search = new URLSearchParams()
  if (params.vehicleId) search.set(TRIPS_QUERY_KEYS.vehicle, params.vehicleId)
  if (params.from) search.set(TRIPS_QUERY_KEYS.from, params.from)
  if (params.to) search.set(TRIPS_QUERY_KEYS.to, params.to)
  const query = search.toString()
  return query.length > 0 ? `${TRIPS_PATH}?${query}` : TRIPS_PATH
}

/** Query keys of a replay opened as part of a "replay every trip" run. */
export const REPLAY_QUERY_KEYS = { playlist: 'playlist', autoplay: 'autoplay' } as const

/**
 * Longest playlist carried in a URL. 36 characters per trip id: 150 trips is ~5.5 kB,
 * comfortably under every browser and proxy limit, and more than a week of a busy van.
 */
export const MAX_PLAYLIST = 150

export function tripReplayPath(
  tripId: string,
  options: { playlist?: readonly string[]; autoplay?: boolean } = {},
): string {
  const search = new URLSearchParams()
  if (options.playlist !== undefined && options.playlist.length > 1) {
    search.set(REPLAY_QUERY_KEYS.playlist, options.playlist.slice(0, MAX_PLAYLIST).join(','))
  }
  if (options.autoplay === true) search.set(REPLAY_QUERY_KEYS.autoplay, '1')
  const query = search.toString()
  return query.length > 0 ? `/trips/replay/${tripId}?${query}` : `/trips/replay/${tripId}`
}

export function mileageReportPath(params: { from?: string; to?: string } = {}): string {
  const search = new URLSearchParams()
  if (params.from) search.set(TRIPS_QUERY_KEYS.from, params.from)
  if (params.to) search.set(TRIPS_QUERY_KEYS.to, params.to)
  const query = search.toString()
  return query.length > 0 ? `${MILEAGE_REPORT_PATH}?${query}` : MILEAGE_REPORT_PATH
}
